import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/takeUntil';
import { ClothingDisposition, ClothingDispositionUnit, ClothingDispositionHistory } from '../../../models/clothing-disposition.model';
import { ClothingDispositionService } from '../../../services/clothing-disposition.service';
import { ClothingDispositionModalComponent } from './clothing-disposition-modal.component';
import { ASSET } from './asset.constants';
import { CLOTHING_DISPOSITION as C } from './clothing-disposition.constants';
import { assetErrorKey, workflowTrackBy } from './asset.helpers';

@Component({selector: 'app-clothing-disposition', templateUrl: './clothing-disposition.component.html',
  styleUrls: ['./employee-assets.component.css']})
export class ClothingDispositionComponent implements OnChanges, OnDestroy {
  trackBy = workflowTrackBy;
  @Input() unitId: string;
  @Input() itemName: string;
  @Input() unitNumber: number;
  @Output() changed = new EventEmitter<void>();
  unit: ClothingDispositionUnit;
  disposition: ClothingDisposition;
  constants = C;
  assetConstants = ASSET;
  loading = false;
  errorKey = ASSET.empty;
  history: ClothingDispositionHistory[] = [];
  historyError = ASSET.empty;
  showHistory = false;
  private request: Subscription;
  private historyRequest: Subscription;
  private dialog: NgbModalRef;
  private generation = ASSET.zero;
  private disposed = false;
  private destroyed = new Subject<void>();
  constructor(private api: ClothingDispositionService, private modals: NgbModal) {}
  get actions(): string[] {
    const retained = [C.agree, C.externalAgree, C.requote, C.deduction, C.exception, C.returnAction,
      C.correct, C.cancel, C.reconcileException, C.reapply, C.resume];
    return this.disposition ? (this.disposition.allowedActions || []).filter(action => retained.indexOf(action) >= ASSET.zero) : [];
  }
  ngOnChanges(): void {
    this.generation++; this.closeDialog(); this.cancelReads(); this.unit = null; this.disposition = null;
    this.history = []; this.showHistory = false; this.historyError = ASSET.empty; this.errorKey = ASSET.empty;
    if (this.unitId) { this.load(); }
  }
  ngOnDestroy(): void {
    this.disposed = true; this.generation++; this.closeDialog(); this.cancelReads(); this.destroyed.next(); this.destroyed.complete();
  }
  load(): void {
    if (this.disposed || !this.unitId || this.dialog) { return; }
    this.cancelReads(); const generation = ++this.generation;
    this.loading = true; this.errorKey = ASSET.empty; this.unit = null; this.disposition = null;
    this.request = this.api.unit(this.unitId).takeUntil(this.destroyed).subscribe(unit => {
      if (!this.current(generation)) { return; }
      this.unit = unit; this.disposition = unit.disposition; this.loading = false;
      if (unit.dispositionId && !unit.disposition) { this.loadCase(generation); }
    }, error => this.fail(generation, error));
  }
  open(action: string): void {
    if (this.disposed || this.loading || this.dialog || !this.permitted(action)) { return; }
    const ref = this.modals.open(ClothingDispositionModalComponent, {size: ASSET.modalSize, beforeDismiss: () => !ref.componentInstance.busy});
    ref.componentInstance.unit = this.unit; ref.componentInstance.disposition = this.disposition;
    ref.componentInstance.itemName = this.unit.catalogItemName || this.itemName;
    ref.componentInstance.unitNumber = this.unit.unitNumber || this.unitNumber;
    ref.componentInstance.action = action; this.dialog = ref;
    ref.result.then(result => this.closed(ref, !!result), () => this.closed(ref, false));
  }
  private permitted(action: string): boolean {
    if (!this.unit) { return false; }
    if (this.disposition) { return this.actions.indexOf(action) >= ASSET.zero; }
    return action === C.retain && this.unit.canRetain || action === C.returnAction && this.unit.canReturn;
  }
  private loadCase(generation: number): void {
    this.loading = true;
    this.request = this.api.detail(this.unit.dispositionId).takeUntil(this.destroyed).subscribe(item => {
      if (!this.current(generation)) { return; }
      this.disposition = item; this.loading = false;
    }, error => this.fail(generation, error));
  }
  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.historyRequest) { this.historyRequest.unsubscribe(); }
    if (!this.showHistory || !this.disposition) { return; }
    const generation = this.generation;
    this.historyError = ASSET.empty;
    this.historyRequest = this.api.history(this.disposition.id).takeUntil(this.destroyed).subscribe(result => {
      if (this.current(generation)) { this.history = result; }
    }, error => { if (this.current(generation)) { this.historyError = assetErrorKey(error, false); } });
  }
  private current(generation: number): boolean { return !this.disposed && generation === this.generation; }
  private fail(generation: number, error: {status?: number}): void {
    if (!this.current(generation)) { return; }
    this.loading = false; this.unit = null; this.disposition = null;
    this.errorKey = error && error.status === ASSET.forbidden ? ASSET.empty : assetErrorKey(error, false);
  }
  private closed(ref: NgbModalRef, changed: boolean): void {
    if (this.dialog !== ref) { return; } this.dialog = null;
    if (!this.disposed && changed) { this.load(); this.changed.emit(); }
  }
  private closeDialog(): void { if (this.dialog) { const ref = this.dialog; this.dialog = null; ref.close(); } }
  private cancelReads(): void {
    if (this.request) { this.request.unsubscribe(); }
    if (this.historyRequest) { this.historyRequest.unsubscribe(); }
    this.loading = false;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/employee-assets/clothing-disposition.component.ts